import { useMemo, useState } from 'react'
import { ArrowUpRightIcon, ArchiveIcon, HistoryIcon, PlusIcon } from 'lucide-react'
import { Box, Link, Stack, TableBody, TableHead, TableRow } from '@mui/material'

import { Button, Card, Input, Modal, Skeleton } from '@repo/ui'
import { SelectField } from 'components'
import { ActionDropdown } from 'components/input/ActionDropdown/ActionDropdown'
import { ViewToggle } from 'components/input/ViewToggle/ViewToggle'
import { useSnackbar } from 'contexts'
import { useCanAccess } from 'hooks'
import { useAddCompetitorPrice, useCompetitorPricing, useRetireCompetitorPrice } from 'hooks/api'
import type { CompetitorBillingPeriod, CompetitorPrice, ResearchCompetitor } from 'services'

import { Cell, EmptyRow, HeadCell, Notice, SectionNote, StatTable, shortDate } from '../InternalAnalyticsPage/components/shared'
import { readOnDate, todayLocal } from './captureDate'
import { CompetitorLink } from './CompetitorLink'

export const PERIOD_LABEL: Record<CompetitorBillingPeriod, string> = {
  month: 'per month',
  year: 'per year',
  one_time: 'one-time',
  usage: 'usage-based',
  custom: 'custom',
}
const PERIODS = Object.keys(PERIOD_LABEL) as CompetitorBillingPeriod[]
const ADMIN_ONLY = 'Only platform admins can change competitor research.'

export const formatPrice = (p: Pick<CompetitorPrice, 'price' | 'currency'>) =>
  p.price === null
    ? 'No public price'
    : new Intl.NumberFormat('en-US', { style: 'currency', currency: p.currency, maximumFractionDigits: 2 }).format(
        p.price,
      )

const SourceLink = ({ url }: { url: string }) => {
  let host = url
  try {
    host = new URL(url).hostname.replace(/^www\./, '')
  } catch {
    /* keep the raw string */
  }
  return (
    <Link
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      color="inherit"
      aria-label={`${host} (opens in a new tab)`}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
    >
      {host}
      <ArrowUpRightIcon size={14} strokeWidth={1} style={{ flexShrink: 0, opacity: 0.6 }} />
    </Link>
  )
}

/**
 * Record a price point. A plan that already has a current price (same name, same billing period)
 * is superseded by the new one, never edited in place, so the history of what a competitor charged
 * survives. The source page and capture date are required: a price is a claim about a competitor.
 */
export const AddPriceModal = ({
  open,
  onClose,
  competitors,
  prices,
  initialDomain,
  initialPlan,
  initialPeriod,
}: {
  open: boolean
  onClose: () => void
  competitors: ResearchCompetitor[]
  prices: CompetitorPrice[]
  initialDomain?: string
  initialPlan?: string
  initialPeriod?: CompetitorBillingPeriod
}) => {
  const add = useAddCompetitorPrice()
  const { open: notify } = useSnackbar()
  const [domain, setDomain] = useState(initialDomain ?? '')
  const [planName, setPlanName] = useState(initialPlan ?? '')
  const [price, setPrice] = useState('')
  const [currency, setCurrency] = useState('USD')
  const [period, setPeriod] = useState<CompetitorBillingPeriod>(initialPeriod ?? 'month')
  const [included, setIncluded] = useState('')
  const [sourceUrl, setSourceUrl] = useState('')
  const [capturedAt, setCapturedAt] = useState(todayLocal())
  const [error, setError] = useState<string | null>(null)

  const close = () => {
    setPlanName('')
    setPrice('')
    setIncluded('')
    setSourceUrl('')
    setCapturedAt(todayLocal())
    setError(null)
    onClose()
  }
  const replaces = prices.find(
    (p) =>
      p.status === 'current' &&
      p.competitorDomain === domain &&
      p.planName.trim().toLowerCase() === planName.trim().toLowerCase() &&
      p.billingPeriod === period,
  )
  const ready = Boolean(domain && planName.trim() && sourceUrl.trim() && capturedAt)

  const submit = () =>
    add.mutate(
      {
        domain,
        input: {
          planName: planName.trim(),
          price: price.trim() === '' ? null : Number(price),
          currency: currency.trim().toUpperCase(),
          billingPeriod: period,
          included: included.trim() || null,
          sourceUrl: sourceUrl.trim(),
          capturedAt,
        },
      },
      {
        onSuccess: (r) => {
          notify({
            type: 'success',
            text: r.superseded ? `Recorded ${r.price.planName}. The previous price is kept as history.` : `Recorded ${r.price.planName}.`,
          })
          close()
        },
        onError: (e) => setError((e as Error).message),
      },
    )

  return (
    <Modal
      open={open}
      onClose={close}
      title="Add price point"
      description="One plan or price, as the competitor's own page states it, with the page and the date you read it."
      actions={
        <>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={!ready} loading={add.isPending}>
            Add price point
          </Button>
        </>
      }
    >
      {error ? (
        <Box sx={{ mb: 3 }}>
          <Notice severity="error">{error}</Notice>
        </Box>
      ) : null}
      <Stack spacing={4}>
        <SelectField
          label="Competitor"
          value={domain}
          search
          placeholder="Choose a competitor"
          onChange={(v) => setDomain(v as string)}
          options={competitors.map((c) => ({ value: c.domain, label: c.name ? `${c.name} · ${c.domain}` : c.domain }))}
          disabled={add.isPending}
        />
        <Input
          label="Plan"
          placeholder="Growth"
          value={planName}
          onChange={setPlanName}
          disabled={add.isPending}
          helperText={
            replaces
              ? `Replaces the current ${formatPrice(replaces)} ${PERIOD_LABEL[replaces.billingPeriod]} price from ${readOnDate(replaces.capturedAt)}, which is kept as history.`
              : undefined
          }
        />
        <Stack direction="row" spacing={3}>
          <Box sx={{ flex: 2 }}>
            <Input
              label="Price"
              placeholder="Leave empty if there is no public price"
              type="number"
              value={price}
              onChange={setPrice}
              disabled={add.isPending}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Input label="Currency" value={currency} onChange={setCurrency} disabled={add.isPending} />
          </Box>
        </Stack>
        <SelectField
          label="Billing period"
          value={period}
          onChange={(v) => setPeriod(v as CompetitorBillingPeriod)}
          options={PERIODS.map((p) => ({ value: p, label: PERIOD_LABEL[p] }))}
          disabled={add.isPending}
        />
        <Input
          label="What is included"
          placeholder="As the pricing page lists it (optional)"
          value={included}
          onChange={setIncluded}
          multiline
          minRows={2}
          disabled={add.isPending}
        />
        <Input
          label="Source page"
          placeholder="https://example.com/pricing"
          value={sourceUrl}
          onChange={setSourceUrl}
          disabled={add.isPending}
        />
        <Input label="Read on" type="date" value={capturedAt} onChange={setCapturedAt} disabled={add.isPending} />
      </Stack>
    </Modal>
  )
}

type View = 'current' | 'history'

/**
 * VRG-744 — the Pricing tab. One row per plan per competitor. "Current" shows the live price of
 * each plan; "History" adds every superseded row, so a price change reads as two dated rows.
 */
export const CompetitorPricingTab = ({ addOpen, onCloseAdd }: { addOpen: boolean; onCloseAdd: () => void }) => {
  const { data, isLoading, error } = useCompetitorPricing()
  const { isHasAccess: isAdmin } = useCanAccess({ role: 'platform_admin' })
  const retire = useRetireCompetitorPrice()
  const { open: notify } = useSnackbar()
  const [view, setView] = useState<View>('current')
  const [addFor, setAddFor] = useState<{ domain: string; planName?: string; period?: CompetitorBillingPeriod } | null>(null)

  const groups = useMemo(() => {
    if (!data) return []
    const rows = data.prices.filter((p) => view === 'history' || p.status === 'current')
    const byDomain = new Map<string, CompetitorPrice[]>()
    for (const p of rows) byDomain.set(p.competitorDomain, [...(byDomain.get(p.competitorDomain) ?? []), p])
    return data.competitors
      .filter((c) => byDomain.has(c.domain))
      .map((c) => ({ competitor: c, prices: byDomain.get(c.domain) ?? [] }))
  }, [data, view])

  if (isLoading)
    return (
      <Card>
        <Skeleton height={14} lines={8} />
      </Card>
    )
  if (error || !data)
    return (
      <Card>
        <SectionNote>{(error as Error | null)?.message ?? 'Could not load pricing.'}</SectionNote>
      </Card>
    )

  const unpriced = data.competitors.filter((c) => !data.prices.some((p) => p.competitorDomain === c.domain))
  const historyCount = data.prices.filter((p) => p.status === 'superseded').length

  return (
    <Stack spacing={6}>
      <Card title="Price points">
        <Stack spacing={4} sx={{ mt: 4 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="space-between" alignItems={{ md: 'center' }}>
            <ViewToggle
              options={[
                { value: 'current', label: 'Current' },
                { value: 'history', label: `With history (${historyCount})` },
              ]}
              value={view}
              onChange={setView}
              ariaLabel="Price view"
            />
          </Stack>
          <SectionNote>
            Each price is read from the competitor&rsquo;s own page and dated. A new price for a plan replaces the
            current one and the old row is kept. {data.capture.note}
          </SectionNote>
          <StatTable>
            <TableHead>
              <TableRow>
                <HeadCell>Competitor</HeadCell>
                <HeadCell>Plan</HeadCell>
                <HeadCell align="right">Price</HeadCell>
                <HeadCell>Billing</HeadCell>
                <HeadCell>Included</HeadCell>
                <HeadCell>Source</HeadCell>
                <HeadCell>Read on</HeadCell>
                {view === 'history' ? <HeadCell>Status</HeadCell> : null}
                <HeadCell align="right"> </HeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.length === 0 ? (
                <EmptyRow colSpan={9} text="No price points yet. Add one from a competitor's pricing page." />
              ) : (
                groups.flatMap(({ competitor, prices }) =>
                  prices.map((p, i) => (
                    <TableRow key={p.id} sx={{ opacity: p.status === 'superseded' ? 0.6 : 1 }}>
                      <Cell strong>{i === 0 ? <CompetitorLink domain={competitor.domain} name={competitor.name} strong /> : ''}</Cell>
                      <Cell>{p.planName}</Cell>
                      <Cell align="right">{formatPrice(p)}</Cell>
                      <Cell>{PERIOD_LABEL[p.billingPeriod]}</Cell>
                      <Cell>
                        <Box sx={{ whiteSpace: 'normal', minWidth: 130, maxWidth: 420, lineHeight: 1.5 }}>{p.included ?? '—'}</Box>
                      </Cell>
                      <Cell>
                        <SourceLink url={p.sourceUrl} />
                      </Cell>
                      <Cell>{readOnDate(p.capturedAt)}</Cell>
                      {view === 'history' ? (
                        <Cell>
                          {p.status === 'current' ? (
                            'Current'
                          ) : (
                            /* Two lines, so the history view fits the card at 1280 (§49 — measured 1009px in 893). */
                            <Box sx={{ lineHeight: 1.4 }}>
                              Superseded
                              {p.supersededAt ? (
                                <Box component="span" sx={{ display: 'block', color: 'text.secondary' }}>
                                  {shortDate(p.supersededAt)}
                                </Box>
                              ) : null}
                            </Box>
                          )}
                        </Cell>
                      ) : null}
                      <Cell align="right">
                        {p.status === 'current' ? (
                          <ActionDropdown
                            items={[
                              {
                                label: 'New price for this plan',
                                icon: <HistoryIcon size={18} strokeWidth={1} />,
                                disabled: !isAdmin,
                                tooltip: !isAdmin ? ADMIN_ONLY : undefined,
                                onClick: () => setAddFor({ domain: competitor.domain, planName: p.planName, period: p.billingPeriod }),
                              },
                              {
                                label: 'No longer offered',
                                icon: <ArchiveIcon size={18} strokeWidth={1} />,
                                divider: true,
                                disabled: !isAdmin || retire.isPending,
                                tooltip: !isAdmin ? ADMIN_ONLY : undefined,
                                onClick: () =>
                                  retire.mutate(p.id, {
                                    onSuccess: () => notify({ type: 'success', text: `${p.planName} is kept as history.` }),
                                    onError: (e) => notify({ type: 'error', text: (e as Error).message }),
                                  }),
                              },
                            ]}
                          />
                        ) : null}
                      </Cell>
                    </TableRow>
                  )),
                )
              )}
            </TableBody>
          </StatTable>
        </Stack>
      </Card>

      {unpriced.length ? (
        <Card title={`No price recorded (${unpriced.length})`}>
          <Stack spacing={4} sx={{ mt: 4 }}>
            <SectionNote>
              Approved competitors with no price point yet. The summary is the one-line Pricing field on the
              competitor&rsquo;s profile, which strategy/competition.md still reads.
            </SectionNote>
            <StatTable>
              <TableHead>
                <TableRow>
                  <HeadCell>Competitor</HeadCell>
                  <HeadCell>Profile summary</HeadCell>
                  <HeadCell align="right"> </HeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unpriced.map((c) => (
                  <TableRow key={c.domain}>
                    <Cell strong>
                      <CompetitorLink domain={c.domain} name={c.name} strong />
                    </Cell>
                    <Cell>
                      <Box sx={{ whiteSpace: 'normal', minWidth: 240, maxWidth: 560, lineHeight: 1.5 }}>
                        {/* The profile drawer's own rendering: competition.md bold markers dropped. */}
                        {c.pricingSummary?.trim() ? c.pricingSummary.replace(/\*\*/g, '') : '—'}
                      </Box>
                    </Cell>
                    <Cell align="right">
                      <Button
                        variant="secondary"
                        startIcon={<PlusIcon size={16} strokeWidth={1.5} />}
                        disabled={!isAdmin}
                        disabledReason={!isAdmin ? ADMIN_ONLY : undefined}
                        onClick={() => setAddFor({ domain: c.domain })}
                      >
                        Add price point
                      </Button>
                    </Cell>
                  </TableRow>
                ))}
              </TableBody>
            </StatTable>
          </Stack>
        </Card>
      ) : null}

      <AddPriceModal
        key={addFor ? `${addFor.domain}:${addFor.planName ?? ''}` : 'any'}
        open={addOpen || addFor !== null}
        onClose={() => {
          setAddFor(null)
          onCloseAdd()
        }}
        competitors={data.competitors}
        prices={data.prices}
        initialDomain={addFor?.domain}
        initialPlan={addFor?.planName}
        initialPeriod={addFor?.period}
      />
    </Stack>
  )
}
